<template>
  <div class="modal-overlay" v-if="visible" @click.self="close">
    <div class="modal-content card-modal">
      <div class="modal-header">
        <span>卡密管理 · {{ skuName || '默认规格（无规格商品）' }}</span>
        <button class="modal-close" @click="close">✕</button>
      </div>

      <div class="modal-body">
        <div class="card-stats">
          <span>卡密总数 <b>{{ total }}</b> 条</span>
          <span>未使用 <b>{{ available }}</b> 条</span>
          <span class="tip">不重复卡密模式下，每张卡只能发放给一个买家</span>
        </div>

        <div class="form-group">
          <label>批量添加卡密（每行一个，单次最多 1000 条）</label>
          <textarea
            v-model="batchText"
            rows="4"
            placeholder="AAAA-BBBB-CCCC&#10;DDDD-EEEE-FFFF"
          ></textarea>
          <button class="add-cards-btn" :disabled="adding" @click="addCards">
            {{ adding ? '添加中...' : '添加到卡密库' }}
          </button>
        </div>

        <div class="card-list">
          <div class="card-item" v-for="card in cards" :key="card.id">
            <span class="card-no">{{ card.card_no }}</span>
            <span class="card-state" :class="{ used: card.is_used === 1 }">
              {{ card.is_used === 1 ? '已使用' : '未使用' }}
              <template v-if="card.send_count > 0">· 已发放 {{ card.send_count }} 次</template>
            </span>
            <button class="card-del" @click="removeCard(card)">删除</button>
          </div>
          <div v-if="loading" class="loading-text">加载中...</div>
          <div v-else-if="!cards.length" class="empty-text">暂无卡密，请先批量添加</div>
        </div>

        <div class="pager" v-if="totalPage > 1">
          <button :disabled="page <= 1" @click="load(page - 1)">上一页</button>
          <span>{{ page }} / {{ totalPage }}</span>
          <button :disabled="page >= totalPage" @click="load(page + 1)">下一页</button>
        </div>
      </div>

      <div class="modal-footer">
        <button class="modal-btn cancel" @click="close">关闭</button>
        <button class="modal-btn confirm" @click="reload">刷新</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getGoodsCardsApi, addGoodsCardsApi, deleteGoodsCardApi } from '@/api/admin'

/**
 * 卡密库管理弹窗（自动发货）
 * 用法：<CardLibraryModal v-model="visible" :goods-id="id" :sku-id="skuId" :sku-name="name" @changed="..." />
 */
export default {
  name: 'CardLibraryModal',
  props: {
    // 弹窗显隐（v-model）
    visible: { type: Boolean, default: false },
    goodsId: { type: [Number, String], default: 0 },
    // 规格 id：0 = 无规格 / 默认卡密池
    skuId: { type: [Number, String], default: 0 },
    skuName: { type: String, default: '' }
  },
  data () {
    return {
      loading: false,
      adding: false,
      cards: [],
      page: 1,
      totalPage: 1,
      total: 0,
      available: 0,
      batchText: ''
    }
  },
  watch: {
    // 打开弹窗 / 切换规格时自动加载卡密
    visible (val) {
      if (val) {
        this.batchText = ''
        this.load(1)
      }
    }
  },
  methods: {
    close () {
      this.$emit('update:visible', false)
    },
    reload () {
      this.load(this.page)
    },
    async load (page = 1) {
      if (!this.goodsId) return
      this.loading = true
      try {
        const { data } = await getGoodsCardsApi(this.goodsId, this.skuId, page, 20)
        this.cards = (data && data.list) || []
        this.total = Number((data && data.total) || 0)
        this.available = Number((data && data.available) || 0)
        this.page = Number((data && data.page) || page)
        this.totalPage = Number((data && data.totalPage) || 1) || 1
      } catch (e) {
        alert('卡密列表加载失败')
      } finally {
        this.loading = false
      }
    },
    async addCards () {
      const text = (this.batchText || '').trim()
      if (!text) {
        alert('请填写卡密内容（每行一个）')
        return
      }
      this.adding = true
      try {
        const { message } = await addGoodsCardsApi(this.goodsId, this.skuId, text)
        this.batchText = ''
        alert(message || '添加成功')
        await this.load(1)
        this.$emit('changed')
      } catch (e) {
        // 错误提示由请求拦截器处理
      } finally {
        this.adding = false
      }
    },
    async removeCard (card) {
      if (!confirm('确定删除该卡密吗？删除后不可恢复。')) return
      try {
        await deleteGoodsCardApi(card.id)
        await this.load(this.page)
        this.$emit('changed')
      } catch (e) {
        alert('删除失败')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  width: 640px;
  max-width: 92vw;
  max-height: 86vh;
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #eef1f5;
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;

  .modal-close {
    border: none;
    background: transparent;
    font-size: 16px;
    color: #98a2b3;
    cursor: pointer;
  }
}

.modal-body {
  padding: 16px 18px;
  overflow-y: auto;
}

.card-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: #5b6673;
  margin-bottom: 12px;

  b {
    color: #ff6b35;
  }

  .tip {
    color: #98a2b3;
    font-size: 12px;
  }
}

.form-group {
  margin-bottom: 14px;

  label {
    display: block;
    font-size: 13px;
    color: #5b6673;
    margin-bottom: 6px;
  }

  textarea {
    width: 100%;
    border: 1px solid #e3e8ef;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    outline: none;
    box-sizing: border-box;

    &:focus {
      border-color: #ffb27a;
    }
  }

  .add-cards-btn {
    margin-top: 8px;
    height: 34px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    background: linear-gradient(90deg, #ecb53c, #ff9211);
    color: #fff;
    font-size: 13px;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.card-list {
  border: 1px solid #eef1f5;
  border-radius: 8px;
  max-height: 260px;
  overflow-y: auto;

  .card-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid #f5f7fa;
    font-size: 13px;

    &:last-child {
      border-bottom: none;
    }

    .card-no {
      flex: 1;
      min-width: 0;
      color: #1f2d3d;
      word-break: break-all;
    }

    .card-state {
      flex-shrink: 0;
      font-size: 12px;
      color: #07c160;

      &.used {
        color: #98a2b3;
      }
    }

    .card-del {
      flex-shrink: 0;
      border: none;
      background: transparent;
      color: #f04438;
      font-size: 12px;
      cursor: pointer;
    }
  }

  .loading-text,
  .empty-text {
    padding: 20px 0;
    text-align: center;
    font-size: 13px;
    color: #98a2b3;
  }
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
  font-size: 13px;
  color: #5b6673;

  button {
    height: 30px;
    padding: 0 12px;
    border: 1px solid #e3e8ef;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 18px;
  border-top: 1px solid #eef1f5;

  .modal-btn {
    height: 34px;
    padding: 0 18px;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;

    &.cancel {
      background: #f2f4f7;
      color: #5b6673;
    }

    &.confirm {
      background: linear-gradient(90deg, #ecb53c, #ff9211);
      color: #fff;
    }
  }
}
</style>
